import * as React from 'react'
import {
  Form,
  Link,
  Box,
  Typography,
  FileUpload,
  Loader,
  ConfirmationModal,
  OutlinedButton,
} from 'cs-kevlar-react'
import { EditModalActionBar } from '../../EditModalActionBar'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Formik } from 'formik'
import {
  MigrationContext,
  MigrationResult,
  MigrationStageStatus,
} from '../../../api'
import clsx from 'clsx'

const statusesThatDisallowCancellation = [
  MigrationStageStatus.Complete,
  MigrationStageStatus.Running,
]

const sampleFile = `{
    
    "Agencies" : {
        "OldExternalId1":["NewExternalId-A"],
        "OldExternalId2":["NewExternalId-B", "NewExternalId-C"],
        "OldExternalId3":["NewExternalId-D", "NewExternalId-E", "NewExternalId-F"],
        ..etc
    },
    "Users": {
        "OldExternalId1":"NewExternalId-A",
        "OldExternalId2":"NewExternalId-B",
        "OldExternalId3":"NewExternalId-C",
        "OldExternalId4":"NewExternalId-D"
        ..etc
    }
}`

interface ConfigureMappingProps {
  onFileSelected: (file: File | null) => void
  uploadMappingFile: () => void
  className?: string
  migrationContext: MigrationContext
  migrationResult: MigrationResult
  onResetDataClicked: (values: any, action: any) => void
  children: any
  onCancel: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      padding: theme.spacing(8),
    },
    scrollable: {
      overflowY: 'auto',
    },
    action: {
      marginTop: theme.spacing(4),
    },
    topMargin: {
      marginTop: theme.spacing(10),
    },
    disclaimer: {
      marginBottom: theme.spacing(16),
    },
  }),
)

const base64EncodedFile = btoa(sampleFile)

const DEFAULT_VALUES = {}

export const ConfigureMapping: React.FunctionComponent<ConfigureMappingProps> = ({
  onFileSelected,
  uploadMappingFile,
  migrationContext,
  migrationResult,
  onResetDataClicked,
  children,
  onCancel,
}) => {
  const classes = useStyles({})
  const [isConfirmingDataReset, setIsConfirmingDataReset] = React.useState<
    boolean
  >(false)
  const [selectedFileName, setSelectedFileName] = React.useState<string | null>(
    null,
  )
  const [lastFileUploadTime, setLastFileUploadTime] = React.useState<number>(0)

  const onReset = (values: any, action: any) => {
    setIsConfirmingDataReset(false)
    setSelectedFileName(null)

    onResetDataClicked && onResetDataClicked(values, action)
  }

  const canResetData =
    migrationContext.stageStatus === MigrationStageStatus.None

  const performUpload = () => {
    uploadMappingFile()
    setSelectedFileName(null)
    setLastFileUploadTime(Date.now())
  }

  const selectFile = (file: File | null) => {
    setSelectedFileName(file?.name ?? null)
    onFileSelected(file)
  }

  return (
    <Formik initialValues={DEFAULT_VALUES} onSubmit={onReset}>
      {(formikProps) => (
        <Form onSubmit={formikProps.handleSubmit} className={classes.root}>
          <Box className={classes.scrollable}>
            <Box className={classes.content}>
              <Box className={classes.disclaimer}>
                <Typography variant="h4">
                  Disconnect current connector before proceeding.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1">
                  A mapping file is required to correctly migrate users,
                  agencies, and devices.{' '}
                  <Link
                    download="sampleMappingFile.txt"
                    href={`data:text/plain;base64,${base64EncodedFile}`}
                    title="Download sample mapping file"
                    target="_blank"
                    rel="noopener"
                  >
                    Download
                  </Link>{' '}
                  a sample mapping file.
                </Typography>
              </Box>
              <Box className={clsx(classes.topMargin, classes.action)}>
                <FileUpload
                  key={lastFileUploadTime.toString()}
                  id="file"
                  onFileSelected={selectFile}
                  showSelectedFileName
                >
                  Choose File
                </FileUpload>
              </Box>
              <Box className={classes.action}>
                <OutlinedButton
                  disabled={selectedFileName === null}
                  onClick={performUpload}
                >
                  Upload File
                </OutlinedButton>
              </Box>
            </Box>
            {formikProps.isSubmitting && (
              <Loader size="sm" text="Uploading Migration Mapping" />
            )}
            {children}
          </Box>
          <Box className={classes.spacer}></Box>
          <EditModalActionBar
            canDelete={false}
            canCancel={
              !statusesThatDisallowCancellation.includes(
                migrationContext.stageStatus,
              )
            }
            onCancel={onCancel}
            onDelete={() => setIsConfirmingDataReset(true)}
            formikProps={formikProps}
            saveColor={
              migrationContext.documentMappingUpdatedOn
                ? 'primary'
                : 'secondary'
            }
            saveText={
              migrationContext.documentMappingUpdatedOn
                ? 'Apply Migration'
                : 'Reset Data'
            }
            canSubmit={canResetData}
          />
          <ConfirmationModal
            onCancel={() => setIsConfirmingDataReset(false)}
            showCancelButton={false}
            onConfirm={onReset as () => void}
            confirmButtonColor="secondary"
            confirmText="Confirm"
            open={isConfirmingDataReset}
            title="Reset Data"
            message={
              <React.Fragment>
                <Typography>Are you sure you want to reset data?</Typography>
                <Typography className={classes.topMargin}>
                  Any unmapped users and agencies will not be migrated and will
                  require manual intervention to re-add agencies, licenses, and
                  reactivate devices.
                </Typography>
              </React.Fragment>
            }
          />
        </Form>
      )}
    </Formik>
  )
}
